import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Trash2,
  ExternalLink,
  Plus,
  Edit,
  Search,
  X,
  Layers,
  Package,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import * as adminApi from "../../api/adminApi";
import { resolveImageUrl } from "../../utils/images";
import "../Auth.css";
import "./AdminCollections.css";

export default function AdminCollections() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'active' | 'inactive'

  // Add modal state
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    arabicName: "",
    description: "",
    arabicDescription: "",
  });
  const [saving, setSaving] = useState(false);

  // Custom Delete modal state
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, productCount }
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      // Fetch collections with a generous page size so admin gets complete overview
      const data = await adminApi.fetchAdminCollections({ page: 1, pageSize: 50 });
      setCollections(data?.items || []);
    } catch (e) {
      addToast(e.message || "Failed to load collections.", "error");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Filtered collections
  const filteredCollections = useMemo(() => {
    return collections.filter((c) => {
      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (c.name && c.name.toLowerCase().includes(q)) ||
        (c.arabicName && c.arabicName.toLowerCase().includes(q)) ||
        (c.description && c.description.toLowerCase().includes(q)) ||
        (c.arabicDescription && c.arabicDescription.toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && c.isEnabled) ||
        (statusFilter === "inactive" && !c.isEnabled);

      return matchesSearch && matchesStatus;
    });
  }, [collections, searchQuery, statusFilter]);

  // Handle Create Collection submission
  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        arabicName: form.arabicName.trim() || null,
        description: form.description.trim() || null,
        arabicDescription: form.arabicDescription.trim() || null,
      };

      const newCollection = await adminApi.createAdminCollection(payload);
      setModal(false);
      setForm({ name: "", arabicName: "", description: "", arabicDescription: "" });
      addToast(`Collection "${newCollection.name || form.name}" created successfully.`, "success");
      // Direct navigation to the edit page to allow image upload and product selection
      navigate(`/admin/collections/${newCollection.id}`);
    } catch (err) {
      addToast(err.message || "Could not create collection.", "error");
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete confirmation execution
  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminApi.deleteAdminCollection(deleteTarget.id);
      addToast(`Collection "${deleteTarget.name}" deleted successfully.`, "success");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      addToast(e.message || "Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-collections">
      {/* Page Header */}
      <div className="admin-collections__head">
        <div>
          <p className="section-label">Catalogue</p>
          <div className="admin-collections__title-wrap">
            <h2 className="admin-collections__title">Collections</h2>
            <span className="admin-collections__count-badge">{collections.length}</span>
          </div>
          <p className="admin-collections__desc">
            Organize products into curated luxury collections for storefront showcases.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary admin-collections__add"
          onClick={() => setModal(true)}
        >
          <Plus size={16} /> New Collection
        </button>
      </div>

      {/* Search & Filter Toolbar */}
      <div className="admin-collections__toolbar">
        <div className="admin-collections__search-box">
          <Search size={16} className="admin-collections__search-icon" />
          <input
            type="text"
            className="admin-collections__search-input"
            placeholder="Search collections by English / Arabic title or description…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              className="admin-collections__clear-search"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="admin-collections__filter-tabs">
          <button
            type="button"
            className={`admin-collections__filter-tab ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All ({collections.length})
          </button>
          <button
            type="button"
            className={`admin-collections__filter-tab ${statusFilter === "active" ? "active" : ""}`}
            onClick={() => setStatusFilter("active")}
          >
            Active ({collections.filter((c) => c.isEnabled).length})
          </button>
          <button
            type="button"
            className={`admin-collections__filter-tab ${statusFilter === "inactive" ? "active" : ""}`}
            onClick={() => setStatusFilter("inactive")}
          >
            Inactive ({collections.filter((c) => !c.isEnabled).length})
          </button>
        </div>
      </div>

      {/* Main Table / Loading State */}
      {loading ? (
        <div className="page-loading">
          <span className="page-loading__dot" />
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: "64px" }}>Cover</th>
                <th>Collection Details</th>
                <th>Products</th>
                <th>Order</th>
                <th>Status</th>
                <th className="admin-table__actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCollections.map((c) => (
                <tr key={c.id}>
                  {/* Thumbnail Cover */}
                  <td>
                    <div className="admin-col-thumb-wrap">
                      {c.imageUrl ? (
                        <img
                          src={resolveImageUrl(c.imageUrl)}
                          alt={c.name}
                          className="admin-col-thumb"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextElementSibling?.classList.remove("hidden");
                          }}
                        />
                      ) : null}
                      <div
                        className={`admin-col-thumb admin-col-thumb--placeholder ${
                          c.imageUrl ? "hidden" : ""
                        }`}
                      >
                        <Layers size={18} />
                      </div>
                    </div>
                  </td>

                  {/* Title & Descriptions */}
                  <td>
                    <div className="admin-col-info">
                      <div className="admin-col-titles">
                        <Link
                          to={`/admin/collections/${c.id}`}
                          className="admin-col-title-en"
                          title="Click to edit collection"
                        >
                          {c.name}
                        </Link>
                        {c.arabicName && (
                          <span className="admin-col-badge-ar" dir="rtl" lang="ar">
                            {c.arabicName}
                          </span>
                        )}
                      </div>

                      {c.description && (
                        <p className="admin-col-desc-text" title={c.description}>
                          {c.description}
                        </p>
                      )}
                      {c.arabicDescription && (
                        <p
                          className="admin-col-desc-text admin-col-desc-text--ar"
                          dir="rtl"
                          lang="ar"
                          title={c.arabicDescription}
                        >
                          {c.arabicDescription}
                        </p>
                      )}
                    </div>
                  </td>

                  {/* Product Count */}
                  <td>
                    <span className="admin-col-count-pill">
                      <Package size={13} />
                      {c.productCount ?? 0} {c.productCount === 1 ? "product" : "products"}
                    </span>
                  </td>

                  {/* Display Order */}
                  <td>
                    <span className="admin-col-order-chip">#{c.displayOrder}</span>
                  </td>

                  {/* Status Badge */}
                  <td>
                    <span
                      className={`status-badge ${
                        c.isEnabled ? "status-active" : "status-inactive"
                      }`}
                    >
                      <span className="status-badge__dot" />
                      {c.isEnabled ? "Active" : "Inactive"}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="admin-table__actions">
                    <div className="admin-collections__row-actions">
                      <Link
                        to={`/admin/collections/${c.id}`}
                        className="admin-icon-btn admin-icon-btn--primary"
                        title="Edit collection"
                      >
                        <Edit size={16} />
                      </Link>
                      <Link
                        to={`/collections/${c.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-icon-btn"
                        title="View collection in store"
                      >
                        <ExternalLink size={16} />
                      </Link>
                      <button
                        type="button"
                        className="admin-icon-btn admin-icon-btn--danger"
                        title="Delete collection"
                        onClick={() =>
                          setDeleteTarget({
                            id: c.id,
                            name: c.name,
                            productCount: c.productCount ?? 0,
                          })
                        }
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {/* Empty state */}
              {filteredCollections.length === 0 && (
                <tr>
                  <td colSpan="6" className="admin-collections__empty-cell">
                    <div className="admin-collections__empty-state">
                      <div className="admin-collections__empty-icon">
                        <Layers size={32} />
                      </div>
                      <h4>No collections found</h4>
                      <p>
                        {searchQuery || statusFilter !== "all"
                          ? "No collections match your current filter criteria."
                          : "You haven't created any collections yet."}
                      </p>
                      {searchQuery || statusFilter !== "all" ? (
                        <button
                          type="button"
                          className="btn-outline btn-sm"
                          onClick={() => {
                            setSearchQuery("");
                            setStatusFilter("all");
                          }}
                        >
                          Clear Filters
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-primary btn-sm"
                          onClick={() => setModal(true)}
                        >
                          <Plus size={14} /> Create First Collection
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: Create Collection */}
      {modal && (
        <div
          className="admin-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-collection-heading"
          onClick={(e) => {
            if (e.target === e.currentTarget && !saving) setModal(false);
          }}
        >
          <div className="admin-modal admin-modal--wide">
            <div className="admin-modal__header">
              <div className="admin-modal__header-icon">
                <Layers size={20} />
              </div>
              <div className="admin-modal__header-text">
                <h3 id="new-collection-heading">Create New Collection</h3>
                <p className="admin-modal__hint">
                  Configure English and Arabic collection details. Banner image and products can be assigned on the next step.
                </p>
              </div>
              <button
                type="button"
                className="admin-modal__close"
                onClick={() => !saving && setModal(false)}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={submitCreate} className="admin-col-form">
              {/* Row 1: Names */}
              <div className="admin-col-form-row">
                <div className="auth-field">
                  <label htmlFor="col-name-en">
                    Title (English) <span className="required-star">*</span>
                  </label>
                  <input
                    id="col-name-en"
                    type="text"
                    placeholder="e.g. Royal Bath Robes"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                    disabled={saving}
                    maxLength={100}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="col-name-ar">
                    Title (Arabic) <span className="optional-tag">(Optional)</span>
                  </label>
                  <input
                    id="col-name-ar"
                    type="text"
                    dir="rtl"
                    placeholder="مثال: أرواب الحمام الملكية"
                    value={form.arabicName}
                    onChange={(e) => setForm((f) => ({ ...f, arabicName: e.target.value }))}
                    disabled={saving}
                    maxLength={100}
                  />
                </div>
              </div>

              {/* Row 2: Descriptions */}
              <div className="admin-col-form-row">
                <div className="auth-field">
                  <div className="auth-field__label-wrap">
                    <label htmlFor="col-desc-en">
                      Description (English) <span className="optional-tag">(Optional)</span>
                    </label>
                    <span className="char-count">{form.description.length}/1000</span>
                  </div>
                  <textarea
                    id="col-desc-en"
                    rows={3}
                    placeholder="Brief curated description for this collection..."
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    disabled={saving}
                    maxLength={1000}
                  />
                </div>

                <div className="auth-field">
                  <div className="auth-field__label-wrap">
                    <label htmlFor="col-desc-ar">
                      Description (Arabic) <span className="optional-tag">(Optional)</span>
                    </label>
                    <span className="char-count">{form.arabicDescription.length}/1000</span>
                  </div>
                  <textarea
                    id="col-desc-ar"
                    rows={3}
                    dir="rtl"
                    placeholder="وصف مختصر للمجموعة يظهر للعملاء..."
                    value={form.arabicDescription}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, arabicDescription: e.target.value }))
                    }
                    disabled={saving}
                    maxLength={1000}
                  />
                </div>
              </div>

              <div className="admin-modal__actions">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving || !form.name.trim()}
                >
                  {saving ? (
                    <>
                      <Loader2 size={16} className="spin-icon" /> Creating…
                    </>
                  ) : (
                    "Create Collection"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Delete Confirmation */}
      {deleteTarget && (
        <div
          className="admin-modal-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-col-heading"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleting) setDeleteTarget(null);
          }}
        >
          <div className="admin-modal admin-modal--danger">
            <div className="admin-modal__header">
              <div className="admin-modal__header-icon admin-modal__header-icon--danger">
                <AlertTriangle size={22} />
              </div>
              <div className="admin-modal__header-text">
                <h3 id="delete-col-heading">Delete Collection</h3>
                <p className="admin-modal__hint">This action cannot be undone.</p>
              </div>
              <button
                type="button"
                className="admin-modal__close"
                onClick={() => !deleting && setDeleteTarget(null)}
                aria-label="Cancel deletion"
              >
                <X size={18} />
              </button>
            </div>

            <div className="admin-modal__delete-body">
              <p>
                Are you sure you want to permanently delete the collection{" "}
                <strong>"{deleteTarget.name}"</strong>?
              </p>
              <div className="admin-modal__danger-notice">
                <ul>
                  <li>
                    <strong>Products:</strong> The{" "}
                    <strong>{deleteTarget.productCount}</strong> products in this collection
                    will be unassigned, but <em>will not be deleted</em> from your store.
                  </li>
                  <li>
                    <strong>Banner:</strong> The collection hero banner image file will be
                    permanently removed from storage.
                  </li>
                </ul>
              </div>
            </div>

            <div className="admin-modal__actions">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={executeDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 size={16} className="spin-icon" /> Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 size={15} /> Delete Collection
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
